import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { SwipeableDrawer } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import EventIcon from '@mui/icons-material/Event';
import GroupsIcon from '@mui/icons-material/Groups';

import { getColor } from '../../Graph/utils';
import { getEdgeCategoryColor, getEdgeCategoryLabel } from '../../edgeCategories';
import { CloudinaryUpload } from '../../CloudinaryUpload';

function getInitials(name = '') {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('') || '?';
}

function formatDate(date) {
  if (!date) return 'Date unknown';
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function formatLifeDates(person) {
  if (!person?.birthDate && !person?.deathDate) return '';
  const born = person.birthDate ? formatDate(person.birthDate) : 'Unknown';
  const died = person.deathDate ? formatDate(person.deathDate) : 'Living';
  return `Born: ${born} - Died: ${died}`;
}

function PersonPhoto({ person, className = '' }) {
  const [didImageFail, setDidImageFail] = useState(false);
  const imageURL = person?.imageURL;

  useEffect(() => {
    setDidImageFail(false);
  }, [imageURL]);

  return (
    <div
      className={`graph-detail-avatar ${className}`}
      style={{ '--detail-accent': getColor(person?._id) }}
      aria-hidden={!person?.name}
    >
      {imageURL && !didImageFail ? (
        <img src={imageURL} alt={person?.name || ''} onError={() => setDidImageFail(true)} />
      ) : (
        <span>{getInitials(person?.name)}</span>
      )}
    </div>
  );
}

function PersonDrawer({
  personSelected,
  idToPerson,
  onEdgeClick,
  open,
  toggleDrawer,
  edges = [],
  onPhotoSuggestionSubmit,
  isSubmittingPhotoSuggestion = false,
  approvedStories = [],
  people = [],
  onStorySuggestionSubmit,
  isSubmittingStorySuggestion = false,
}) {
  const [photoUrl, setPhotoUrl] = useState('');
  const [submitterName, setSubmitterName] = useState('');
  const [submitterEmail, setSubmitterEmail] = useState('');
  const [submitterNote, setSubmitterNote] = useState('');
  const [photoPreviewFailed, setPhotoPreviewFailed] = useState(false);
  const [photoStatus, setPhotoStatus] = useState('');
  const [photoError, setPhotoError] = useState('');
  const [storyTitle, setStoryTitle] = useState('');
  const [storyText, setStoryText] = useState('');
  const [storyDate, setStoryDate] = useState('');
  const [storyTagQuery, setStoryTagQuery] = useState('');
  const [taggedPersonIds, setTaggedPersonIds] = useState([]);
  const [storySubmitterName, setStorySubmitterName] = useState('');
  const [storySubmitterEmail, setStorySubmitterEmail] = useState('');
  const [storySubmitterNote, setStorySubmitterNote] = useState('');
  const [storyStatus, setStoryStatus] = useState('');
  const [storyError, setStoryError] = useState('');
  const [isSuggestionPanelOpen, setIsSuggestionPanelOpen] = useState(false);

  const relatedStories = useMemo(() => (
    edges
      .filter(Boolean)
      .map((edge) => {
        const otherPersonId = edge?.person1 !== personSelected?._id ? edge?.person1 : edge?.person2;
        return {
          edge,
          otherPerson: idToPerson?.[otherPersonId],
        };
      })
  ), [edges, idToPerson, personSelected?._id]);
  const totalStoryCount = relatedStories.length + approvedStories.length;

  const handleClose = () => {
    toggleDrawer(false)();
  };

  const sortedPeople = useMemo(() => (
    [...people].sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }))
  ), [people]);

  const selectedTaggedPeople = useMemo(() => (
    taggedPersonIds.map((personId) => idToPerson?.[personId]).filter(Boolean)
  ), [idToPerson, taggedPersonIds]);

  const tagOptions = useMemo(() => {
    const normalizedQuery = storyTagQuery.trim().toLowerCase();
    return sortedPeople
      .filter((person) => person._id !== personSelected?._id)
      .filter((person) => !taggedPersonIds.includes(person._id))
      .filter((person) => !normalizedQuery || (person.name || '').toLowerCase().includes(normalizedQuery))
      .slice(0, 6);
  }, [personSelected?._id, sortedPeople, storyTagQuery, taggedPersonIds]);

  useEffect(() => {
    setPhotoUrl('');
    setSubmitterName('');
    setSubmitterEmail('');
    setSubmitterNote('');
    setPhotoPreviewFailed(false);
    setPhotoStatus('');
    setPhotoError('');
    setStoryTitle('');
    setStoryText('');
    setStoryDate('');
    setStoryTagQuery('');
    setTaggedPersonIds([]);
    setStorySubmitterName('');
    setStorySubmitterEmail('');
    setStorySubmitterNote('');
    setStoryStatus('');
    setStoryError('');
    setIsSuggestionPanelOpen(false);
  }, [personSelected?._id]);

  const updatePhotoUrl = useCallback((nextUrl) => {
    setPhotoUrl(nextUrl || '');
    setPhotoPreviewFailed(false);
    setPhotoStatus('');
    setPhotoError('');
  }, []);

  const submitPhotoSuggestion = async (event) => {
    event.preventDefault();
    if (!personSelected?._id || !photoUrl.trim() || !onPhotoSuggestionSubmit) return;

    setPhotoStatus('');
    setPhotoError('');
    try {
      const result = await onPhotoSuggestionSubmit({
        personId: personSelected._id,
        imageUrl: photoUrl,
        submitterName,
        submitterEmail,
        submitterNote,
      });
      setPhotoUrl('');
      setSubmitterName('');
      setSubmitterEmail('');
      setSubmitterNote('');
      setPhotoPreviewFailed(false);
      setPhotoStatus(result?.approved ? 'Photo approved and updated.' : 'Photo sent for approval.');
    } catch (error) {
      setPhotoError(error.message || 'Could not send this photo.');
    }
  };

  const addStoryTag = (personId) => {
    setTaggedPersonIds((current) => current.includes(personId) ? current : [...current, personId]);
    setStoryTagQuery('');
    setStoryStatus('');
    setStoryError('');
  };

  const removeStoryTag = (personId) => {
    setTaggedPersonIds((current) => current.filter((id) => id !== personId));
    setStoryStatus('');
    setStoryError('');
  };

  const submitStorySuggestion = async (event) => {
    event.preventDefault();
    if (!personSelected?._id || !storyText.trim() || !onStorySuggestionSubmit) return;

    setStoryStatus('');
    setStoryError('');
    try {
      const result = await onStorySuggestionSubmit({
        personId: personSelected._id,
        title: storyTitle,
        story: storyText,
        storyDate,
        taggedPersonIds,
        submitterName: storySubmitterName,
        submitterEmail: storySubmitterEmail,
        submitterNote: storySubmitterNote,
      });
      setStoryTitle('');
      setStoryText('');
      setStoryDate('');
      setStoryTagQuery('');
      setTaggedPersonIds([]);
      setStorySubmitterName('');
      setStorySubmitterEmail('');
      setStorySubmitterNote('');
      setStoryStatus(result?.approved ? 'Story approved and added.' : 'Story sent for approval.');
    } catch (error) {
      setStoryError(error.message || 'Could not send this story.');
    }
  };

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={toggleDrawer(false)}
      onOpen={toggleDrawer(true)}
      swipeAreaWidth={personSelected ? 48 : 0}
      disableSwipeToOpen={false}
      ModalProps={{ keepMounted: true }}
      PaperProps={{
        sx: {
          height: 'min(86dvh, calc(100dvh - env(safe-area-inset-top, 0px)))',
          overflow: 'visible',
          borderTopLeftRadius: 24,
          borderTopRightRadius: 24,
          background: 'transparent',
          boxShadow: 'none',
        },
      }}
    >
      <section
        className="graph-detail-sheet graph-person-sheet"
        style={{
          '--detail-accent': getColor(personSelected?._id),
          '--detail-accent-soft': `color-mix(in srgb, ${getColor(personSelected?._id)} 24%, transparent)`,
        }}
        aria-label={personSelected ? `View ${personSelected.name}` : 'View person'}
      >
        <div className="graph-detail-puller" />
        <button type="button" className="graph-detail-close" onClick={handleClose} aria-label="Close person details">
          <CloseIcon fontSize="small" />
        </button>

        <header className="graph-person-hero">
          <PersonPhoto person={personSelected} className="graph-detail-avatar--hero" />
          <div className="graph-person-hero-copy">
            <span>Person</span>
            <h2>{personSelected?.name || 'Unknown person'}</h2>
            {formatLifeDates(personSelected) && <small>{formatLifeDates(personSelected)}</small>}
            <p>{personSelected?.funFact || 'No fun fact has been added yet.'}</p>
          </div>
        </header>

        <div className="graph-detail-stats" aria-label="Person stats">
          <div>
            <GroupsIcon fontSize="small" />
            <strong>{relatedStories.length}</strong>
            <span>{relatedStories.length === 1 ? 'Connection' : 'Connections'}</span>
          </div>
          <div>
            <AutoStoriesIcon fontSize="small" />
            <strong>{totalStoryCount}</strong>
            <span>{totalStoryCount === 1 ? 'Story' : 'Stories'}</span>
          </div>
        </div>

        <div className="graph-detail-section-header">
          <span>Stories</span>
          <strong>Memories people have shared</strong>
        </div>

        <div className="graph-person-memory-list">
          {approvedStories.length ? approvedStories.map((story) => (
            <article key={story._id} className="graph-person-memory-card">
              <div>
                <span>{story.title || 'Shared story'}</span>
                <strong>{story.story}</strong>
              </div>
              {story.storyDate && (
                <small className="graph-person-memory-date">
                  <EventIcon fontSize="inherit" />
                  {formatDate(story.storyDate)}
                </small>
              )}
              {story.taggedPersonIds?.length ? (
                <div className="graph-person-memory-tags">
                  {story.taggedPersonIds.map((personId) => (
                    <em key={personId}>{idToPerson?.[personId]?.name || 'Someone'}</em>
                  ))}
                </div>
              ) : null}
              <small>
                {story.submitterName ? `Shared by ${story.submitterName}` : 'Shared by someone in the group'}
              </small>
            </article>
          )) : (
            <div className="graph-detail-empty">
              No personal stories have been added yet.
            </div>
          )}
        </div>

        <div className="graph-detail-section-header">
          <span>Connections</span>
          <strong>How they fit into the map</strong>
        </div>

        <div className="graph-person-story-list">
          {relatedStories.length ? relatedStories.map(({ edge, otherPerson }) => (
            <button
              type="button"
              key={edge._id}
              className="graph-person-story-card"
              onClick={() => onEdgeClick(edge)}
              style={{
                '--detail-accent': getColor(otherPerson?._id),
                '--edge-category-color': getEdgeCategoryColor(edge?.category),
              }}
            >
              <PersonPhoto person={otherPerson} className="graph-detail-avatar--story" />
              <div className="graph-person-story-copy">
                <div className="graph-person-story-title">
                  <strong>{otherPerson?.name || 'Unknown person'}</strong>
                  <span>{getEdgeCategoryLabel(edge?.category)}</span>
                </div>
                <small>
                  <EventIcon fontSize="inherit" />
                  {formatDate(edge?.dateMet)}
                </small>
                <p>{edge?.notes || 'No story has been added for this connection yet.'}</p>
              </div>
              <ArrowForwardIcon className="graph-person-story-arrow" fontSize="small" />
            </button>
          )) : (
            <div className="graph-detail-empty">
              No stories have been added for this person yet.
            </div>
          )}
        </div>

        <div className="graph-person-suggestion-footer">
          {!isSuggestionPanelOpen ? (
            <button
              type="button"
              className="graph-person-suggest-edit"
              onClick={() => setIsSuggestionPanelOpen(true)}
            >
              Suggest an edit
            </button>
          ) : (
            <div className="graph-person-suggestion-panel">
              <div className="graph-person-suggestion-header">
                <div>
                  <span>Suggest an edit</span>
                  <strong>Send updates for approval</strong>
                </div>
                <button type="button" onClick={() => setIsSuggestionPanelOpen(false)}>
                  Done
                </button>
              </div>

              <form className="graph-person-photo-request" onSubmit={submitPhotoSuggestion}>
                <div className="graph-detail-section-header">
                  <span>Photo</span>
                  <strong>Suggest a better profile photo</strong>
                </div>
                <div className="graph-person-photo-request-body">
                  <div
                    className="graph-person-photo-preview"
                    style={{ '--detail-accent': getColor(personSelected?._id) }}
                    aria-hidden="true"
                  >
                    {photoUrl && !photoPreviewFailed ? (
                      <img src={photoUrl} alt="" onError={() => setPhotoPreviewFailed(true)} />
                    ) : (
                      <span>{getInitials(personSelected?.name)}</span>
                    )}
                  </div>
                  <div className="graph-person-photo-fields">
                    <label>
                      <span>New photo</span>
                      <input
                        value={photoUrl}
                        onChange={(event) => updatePhotoUrl(event.target.value)}
                        placeholder="Paste a photo URL or upload"
                      />
                    </label>
                    <CloudinaryUpload imageUrl={photoUrl} setImageUrl={updatePhotoUrl} />
                    {photoPreviewFailed && (
                      <small className="graph-person-photo-error">That photo could not load. Try another URL or upload a new photo.</small>
                    )}
                    <div className="graph-person-photo-contact">
                      <label>
                        <span>Your name</span>
                        <input
                          value={submitterName}
                          onChange={(event) => setSubmitterName(event.target.value)}
                          placeholder="Optional"
                        />
                      </label>
                      <label>
                        <span>Your email</span>
                        <input
                          type="email"
                          value={submitterEmail}
                          onChange={(event) => setSubmitterEmail(event.target.value)}
                          placeholder="Optional"
                        />
                      </label>
                    </div>
                    <label>
                      <span>Note</span>
                      <input
                        value={submitterNote}
                        onChange={(event) => setSubmitterNote(event.target.value)}
                        placeholder="Optional"
                      />
                    </label>
                    <button
                      type="submit"
                      className="graph-person-photo-submit"
                      disabled={!photoUrl.trim() || isSubmittingPhotoSuggestion}
                    >
                      {isSubmittingPhotoSuggestion ? 'Sending...' : 'Send photo for approval'}
                    </button>
                    {photoStatus && <small className="graph-person-photo-status">{photoStatus}</small>}
                    {photoError && <small className="graph-person-photo-error">{photoError}</small>}
                  </div>
                </div>
              </form>

              <form className="graph-person-story-request" onSubmit={submitStorySuggestion}>
                <div className="graph-detail-section-header">
                  <span>Add a story</span>
                  <strong>Share a memory about {personSelected?.name || 'this person'}</strong>
                </div>
                <label>
                  <span>Title</span>
                  <input
                    value={storyTitle}
                    onChange={(event) => setStoryTitle(event.target.value)}
                    placeholder="Optional"
                  />
                </label>
                <label>
                  <span>Story date</span>
                  <input
                    type="date"
                    value={storyDate}
                    onChange={(event) => setStoryDate(event.target.value)}
                  />
                </label>
                <label>
                  <span>Story</span>
                  <textarea
                    value={storyText}
                    onChange={(event) => setStoryText(event.target.value)}
                    placeholder="Tell the story..."
                    required
                  />
                </label>
                <div className="graph-person-tag-picker">
                  <label>
                    <span>People who were there</span>
                    <input
                      value={storyTagQuery}
                      onChange={(event) => setStoryTagQuery(event.target.value)}
                      placeholder="Search people to tag"
                    />
                  </label>
                  {storyTagQuery && (
                    <div className="graph-person-tag-options">
                      {tagOptions.length ? tagOptions.map((person) => (
                        <button type="button" key={person._id} onClick={() => addStoryTag(person._id)}>
                          {person.name}
                        </button>
                      )) : (
                        <small>No matches</small>
                      )}
                    </div>
                  )}
                  {selectedTaggedPeople.length ? (
                    <div className="graph-person-tag-chips">
                      {selectedTaggedPeople.map((person) => (
                        <button type="button" key={person._id} onClick={() => removeStoryTag(person._id)}>
                          {person.name}
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
                <div className="graph-person-photo-contact">
                  <label>
                    <span>Your name</span>
                    <input
                      value={storySubmitterName}
                      onChange={(event) => setStorySubmitterName(event.target.value)}
                      placeholder="Optional"
                    />
                  </label>
                  <label>
                    <span>Your email</span>
                    <input
                      type="email"
                      value={storySubmitterEmail}
                      onChange={(event) => setStorySubmitterEmail(event.target.value)}
                      placeholder="Optional"
                    />
                  </label>
                </div>
                <label>
                  <span>Note for Kevin</span>
                  <input
                    value={storySubmitterNote}
                    onChange={(event) => setStorySubmitterNote(event.target.value)}
                    placeholder="Optional"
                  />
                </label>
                <button
                  type="submit"
                  className="graph-person-photo-submit"
                  disabled={!storyText.trim() || isSubmittingStorySuggestion}
                >
                  {isSubmittingStorySuggestion ? 'Sending...' : 'Send story for approval'}
                </button>
                {storyStatus && <small className="graph-person-photo-status">{storyStatus}</small>}
                {storyError && <small className="graph-person-photo-error">{storyError}</small>}
              </form>
            </div>
          )}
        </div>
      </section>
    </SwipeableDrawer>
  );
}

export default PersonDrawer;
